export type RecipeTag = {
  path: string[];
};

export function formatTagPath(path: readonly string[]): string {
  return path.map((segment) => segment.trim()).filter((segment) => segment.length > 0).join(" › ");
}

export function parseTagPathInput(raw: string): string[] | null {
  const segments = raw
    .split(/(?:\s*>\s*|\s*\/\s*)/u)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  return segments.length > 0 ? segments : null;
}

export function tagPathKey(path: readonly string[]): string {
  return path.map((segment) => segment.toLowerCase()).join("\0");
}

export function normalizeRecipeTags(tags: readonly RecipeTag[] | undefined): RecipeTag[] {
  if (tags === undefined || tags.length === 0) {
    return [];
  }

  const seen = new Set<string>();
  const normalized: RecipeTag[] = [];
  for (const tag of tags) {
    const segments = tag.path.map((segment) => segment.trim()).filter((segment) => segment.length > 0);
    if (segments.length === 0) {
      continue;
    }
    const key = tagPathKey(segments);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    normalized.push({ path: segments });
  }
  return normalized.sort((left, right) => formatTagPath(left.path).localeCompare(formatTagPath(right.path)));
}

export function ancestorPaths(path: readonly string[]): string[][] {
  const ancestors: string[][] = [];
  for (let index = 1; index < path.length; index += 1) {
    ancestors.push(path.slice(0, index));
  }
  return ancestors;
}

export function addTagPath(tags: readonly RecipeTag[], path: readonly string[]): RecipeTag[] {
  const segments = path.map((segment) => segment.trim()).filter((segment) => segment.length > 0);
  if (segments.length === 0) {
    return normalizeRecipeTags(tags);
  }

  const next = [...tags, { path: segments }];
  for (const ancestor of ancestorPaths(segments)) {
    next.push({ path: ancestor });
  }
  return normalizeRecipeTags(next);
}

export function removeTagPath(tags: readonly RecipeTag[], path: readonly string[]): RecipeTag[] {
  const key = tagPathKey(path);
  return normalizeRecipeTags(tags.filter((tag) => tagPathKey(tag.path) !== key));
}

export function tagPathMatchesFilter(recipePath: readonly string[], filterPath: readonly string[]): boolean {
  if (filterPath.length > recipePath.length) {
    return false;
  }
  return filterPath.every((segment, index) => segment.toLowerCase() === recipePath[index]?.toLowerCase());
}

export function recipeMatchesTagFilter(tags: readonly RecipeTag[] | undefined, filterPath: readonly string[]): boolean {
  if (filterPath.length === 0) {
    return true;
  }
  return (tags ?? []).some((tag) => tagPathMatchesFilter(tag.path, filterPath));
}

export function collectTagPaths(tags: readonly RecipeTag[] | undefined): string[][] {
  return normalizeRecipeTags(tags).map((tag) => tag.path);
}

export function collectKnownTagPaths(recipes: readonly { tags?: readonly RecipeTag[] }[]): string[][] {
  const seen = new Map<string, string[]>();
  for (const recipe of recipes) {
    for (const tag of normalizeRecipeTags(recipe.tags)) {
      seen.set(tagPathKey(tag.path), tag.path);
    }
  }
  return [...seen.values()].sort((left, right) => formatTagPath(left).localeCompare(formatTagPath(right)));
}

export function recipeMatchesSearchQuery(
  recipe: { title: string; tags?: readonly RecipeTag[] },
  query: string
): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return true;
  }

  if (recipe.title.toLowerCase().includes(needle)) {
    return true;
  }

  return (recipe.tags ?? []).some((tag) => formatTagPath(tag.path).toLowerCase().includes(needle));
}
